import { useContext, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';

import { Input, Modal } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Button, Label, LABEL_TYPE, Spinner, Textarea } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { TagValue } from '@/components/generic/Tag/tag-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext, useOfflineContext } from '@/contexts';
import { PERMISSION_TYPE, PlaylistType, PRIVATE_PAGE_ROUTES } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getPermissionsForOrganization } from '@/helpers';
import { formatDurationToMinutes } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { Music } from '@/pages/Event/components/Music';
import { playlistValidationSchema } from '@/pages/Playlists/PlaylistValidation';
import { BlueDownloadIcon, EditPenIcon, TrashIcon } from '@/resources';
import { Playlists } from '@/resources/icons/sidebar';
import { useGetOrganizationByIdSimplifiedQuery } from '@/services';
import {
  useDeletePlaylistMutation,
  useGetPlaylistById,
  useUpdateBasicPlaylistMutation,
  useUpdatePlaylistMutation
} from '@/services/playlist.service';
import { Asset, CreateEditPlaylistAssetPayload, PlaylistAsset } from '@/types';

import { PlaylistMixDetails } from './PlaylistMixDetails';

type PlaylistDetailsProps = {
  playlistType?: PlaylistType;
};

export const PlaylistDetails = ({ playlistType = PlaylistType.MUSIC }: PlaylistDetailsProps) => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { organizationPermissions } = useContext(AuthContext);
  const {
    downloadPlaylist,
    downloadedPlaylistIds,
    downloadProgress,
    isOnline,
    getOfflinePlaylist,
    removeOfflinePlaylist
  } = useOfflineContext();

  const offlinePlaylistData = getOfflinePlaylist(Number(playlistId));
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedSongs, setSelectedSongs] = useState<Asset[]>([]);
  const [chosenSongs, setChosenSongs] = useState<CreateEditPlaylistAssetPayload[]>([]);
  const [initialChosenSongs, setInitialChosenSongs] = useState<CreateEditPlaylistAssetPayload[]>(
    []
  );
  const [organizationId, setOrganizationId] = useState<number>();
  const [error, setError] = useState<number>();

  const { data: playlistOrganization } = useGetOrganizationByIdSimplifiedQuery(
    Number(organizationId ?? 0)
  );
  const hasOfflineMusicDownloadAccess = getPermissionsForOrganization(
    organizationPermissions,
    playlistOrganization?.data
  ).includes(ORGANIZATION_PERMISSION_TYPE.OFFLINE_MUSIC_DOWNLOAD);

  const isDownloadingPlaylist = Object.keys(downloadProgress).length > 0;

  const {
    data: currentPlaylist,
    isLoading,
    isError
  } = useGetPlaylistById(Number(playlistId), {
    onSuccess: () => undefined,
    onError: (error) => setError(error.status)
  });
  const shouldShowLoading = isOnline && isLoading;
  const shouldShowError = isOnline && !!error;
  const { mutate: deletePlaylist } = useDeletePlaylistMutation(queryClient, {
    onSuccess: async () => {
      const deletedPlaylistId = currentPlaylist?.data?.id;
      if (deletedPlaylistId) {
        try {
          await removeOfflinePlaylist(deletedPlaylistId);
        } catch {
          // Ignore offline cleanup errors and continue with normal flow.
        }
      }

      navigate(
        playlistType === PlaylistType.MIX
          ? PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_PLAYLISTS
          : PRIVATE_PAGE_ROUTES.PLAYLISTS
      );
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const { mutate: updatePlaylist } = useUpdatePlaylistMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('playlistSuccesfullyUpdated'));
      setIsSubmitting(false);
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: updateBasicPlaylist } = useUpdateBasicPlaylistMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('playlistSuccesfullyUpdated'));
      setIsSubmitting(false);
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    if (currentPlaylist?.data) {
      if (currentPlaylist.data.playlistType.toUpperCase() === PlaylistType[PlaylistType.MUSIC]) {
        const playlistAssets = currentPlaylist.data.playlistAssets ?? [];

        const assets = playlistAssets.map((pa: PlaylistAsset) => pa.asset);
        setSelectedSongs(assets);

        const chosen = playlistAssets.map((pa: PlaylistAsset) => ({
          id: pa.id,
          playlistId: pa.playlistId,
          assetId: pa.asset.id,
          order: pa.order
        }));
        setChosenSongs(chosen);
        setInitialChosenSongs(chosen);
      }
      setOrganizationId(currentPlaylist.data.organizationId);
    }
  }, [currentPlaylist]);

  const handleUserSetSelectedSongs = (songs: Asset[] | ((prevState: Asset[]) => Asset[])) => {
    const newSongs = typeof songs === 'function' ? songs(selectedSongs) : songs;
    setSelectedSongs(newSongs);
    setChosenSongs(
      newSongs.map((asset, index) => ({
        id: null,
        playlistId: Number(playlistId),
        assetId: asset.id,
        order: index + 1
      }))
    );
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsOpenDeleteModal(true);
  };

  const isChanged = useMemo(() => {
    return !isEqual(initialChosenSongs, chosenSongs);
  }, [initialChosenSongs, chosenSongs]);

  useEffect(() => {
    if (isChanged) {
      handleSubmit();
    }
  }, [isChanged]);

  const handleSubmit = () => {
    const totalDuration = selectedSongs.reduce((sum, asset) => sum + (asset.duration ?? 0), 0);
    const payload = {
      id: currentPlaylist?.data.id,
      organizationId: currentPlaylist?.data.organizationId ?? null,
      name: currentPlaylist?.data.name,
      description: currentPlaylist?.data.description ?? '',
      playlistAssets: chosenSongs,
      totalDuration: totalDuration,
      playlistType: playlistType === PlaylistType.MIX ? PlaylistType.MIX : PlaylistType.MUSIC
    };
    updatePlaylist(payload);
  };

  const handleSubmitForm = (values: { name: string; description: string }) => {
    setIsSubmitting(true);
    const payload = {
      id: currentPlaylist?.data.id,
      name: values.name,
      description: values.description
    };

    updateBasicPlaylist(payload);
  };

  const getInitialValues = () => {
    return {
      name: currentPlaylist?.data.name,
      description: currentPlaylist?.data.description || ''
    };
  };

  const handleDownloadPlaylist = () => {
    if (currentPlaylist?.data) {
      void downloadPlaylist(currentPlaylist.data);
    }
  };

  const headerButtonsConfig: ButtonConfig[] = [
    ...(playlistType === PlaylistType.MUSIC &&
    hasOfflineMusicDownloadAccess &&
    currentPlaylist?.data &&
    !downloadedPlaylistIds.has(currentPlaylist.data.id)
      ? [
          {
            icon: (
              <BlueDownloadIcon
                className={`text-primary-blue ${isDownloadingPlaylist ? 'animate-pulse opacity-50' : ''}`}
              />
            ),
            variant: IconButtonVariant.PRIMARY,
            type: BUTTON_TYPE.ICON_BUTTON,
            handleOnClick: handleDownloadPlaylist,
            isDisabled: isDownloadingPlaylist
          }
        ]
      : []),
    ...(isOnline
      ? [
          {
            icon: <EditPenIcon className='text-primary-blue' />,
            variant: IconButtonVariant.PRIMARY,
            type: BUTTON_TYPE.ICON_BUTTON,
            handleOnClick: handleEdit,
            permissions: [PERMISSION_TYPE.UPDATE_PLAYLISTS]
          },
          {
            icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
            variant: IconButtonVariant.PRIMARY,
            type: BUTTON_TYPE.ICON_BUTTON,
            handleOnClick: handleDelete,
            permissions: [PERMISSION_TYPE.DELETE_PLAYLISTS]
          }
        ]
      : [])
  ];

  return (
    <>
      {shouldShowLoading ? (
        <Spinner />
      ) : shouldShowError ? (
        <ErrorPage code={error} />
      ) : !isOnline && !offlinePlaylistData ? (
        <div className='flex h-full items-center justify-center'>
          <Label
            text={t('playlistNotDownloaded')}
            type={LABEL_TYPE.PARAGRAPH2}
            className='text-secondary-steel-blue'
          />
        </div>
      ) : (
        (!isOnline || !isError) && (
          <DetailsPageLayout
            icon={<Playlists className='text-primary-blue' />}
            iconPath={
              playlistType === PlaylistType.MIX
                ? PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_PLAYLISTS
                : PRIVATE_PAGE_ROUTES.PLAYLISTS
            }
            path={[{ label: currentPlaylist?.data?.name ?? offlinePlaylistData?.name ?? '' }]}
            title={currentPlaylist?.data?.name ?? offlinePlaylistData?.name ?? ''}
            isLoading={shouldShowLoading}
            tag={
              currentPlaylist?.data && downloadedPlaylistIds.has(currentPlaylist.data.id)
                ? TagValue.DOWNLOADED_FOR_OFFLINE
                : undefined
            }
            headerButtons={headerButtonsConfig}
            childrenClassName='h-full flex-col'>
            {playlistType === PlaylistType.MUSIC && (
              <>
                <div className='flex w-full flex-col gap-4'>
                  {isOnline && (
                    <div className='flex w-full flex-col md:flex-row md:items-center'>
                      <Label
                        text={`${t('organization')}:`}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                      />
                      <Label
                        text={currentPlaylist?.data?.organization?.name}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='!text-primary-black'
                      />
                    </div>
                  )}
                  <div className='flex w-full flex-col md:flex-row md:items-center'>
                    <Label
                      text={`${t('description')}:`}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                    />
                    <Label
                      text={`${currentPlaylist?.data?.description ?? offlinePlaylistData?.description ?? ''}`}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='!text-primary-black'
                    />
                  </div>
                  {(currentPlaylist?.data?.totalDuration !== undefined ||
                    offlinePlaylistData?.totalDuration !== undefined) && (
                    <div className='flex w-full flex-col md:flex-row md:items-center'>
                      <Label
                        text={`${t('playlistDuration')}:`}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                      />
                      <Label
                        text={formatDurationToMinutes(
                          currentPlaylist?.data?.totalDuration ?? offlinePlaylistData?.totalDuration
                        )}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='!text-primary-black'
                      />
                    </div>
                  )}
                </div>
                <Music
                  hasEvent={false}
                  isTab
                  setPlaylistAssets={handleUserSetSelectedSongs}
                  playlistAssets={selectedSongs}
                  organizationId={organizationId}
                  playlistId={Number(playlistId)}
                />
              </>
            )}
            {playlistType === PlaylistType.MIX && (
              <DndProvider backend={HTML5Backend}>
                <PlaylistMixDetails
                  currentPlaylist={currentPlaylist?.data}
                  updatePlaylist={updatePlaylist}
                />
              </DndProvider>
            )}
          </DetailsPageLayout>
        )
      )}
      {isOpenDeleteModal && (
        <ConfirmationModal
          onConfirm={() => deletePlaylist(currentPlaylist?.data?.id)}
          isOpen={isOpenDeleteModal}
          onClose={() => setIsOpenDeleteModal(false)}
          message={t('deleteMessage', { item: currentPlaylist?.data.name })}
        />
      )}
      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          modalClasses='w-full md:w-[500px] '
          label={t('editPlaylist')}
          height=''>
          <div className='inline-flex size-full flex-col items-center justify-center'>
            <Formik
              enableReinitialize
              onSubmit={handleSubmitForm}
              initialValues={getInitialValues()}
              validationSchema={playlistValidationSchema}>
              {({ errors, touched, isValid, dirty }) => (
                <Form className='size-full overflow-hidden scrollbar-none'>
                  <div className='flex w-full flex-col gap-y-2'>
                    <Input
                      type='text'
                      id='name'
                      label={t('name')}
                      isRequired
                      errorMessage={errors.name as string}
                      isInvalid={!!(errors.name && touched.name)}
                    />
                    <Textarea
                      id='description'
                      label={t('description')}
                      errorMessage={errors.description as string}
                      className='h-20'
                    />
                  </div>
                  <div className='mt-2 flex justify-end'>
                    <Button
                      type='submit'
                      className='w-full md:w-fit'
                      variant={ButtonVariant.PRIMARY}
                      disabled={isSubmitting || !(isValid && dirty)}>
                      {t('save')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}
    </>
  );
};
